import {
  type SortDirection,
  compareNullsLast,
  sortFactor,
} from "~/pages/manifold-nextgen/components/evals/TableSort";
import {
  EVALUATORS,
  EVALUATOR_ORDER,
  EvaluatorKind,
} from "~/pages/manifold-nextgen/components/evals/constants/evaluators";
import { CaseOutcome } from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import { CaseSortKey } from "~/pages/manifold-nextgen/components/evals/constants/sorting";
import type { PartialEvalCase, PartialEvalCaseResult, PartialEvalRun } from "~/services/evals";
import { parseApiTimestamp } from "./core";
import { isNumber, mean } from "./math";

/** A case as the cases tab reads it: its own run history, and how it is defined. */
/** One cell of the case-health matrix: a case's outcome in one run. */
export interface CaseHealthCell {
  runId: string;
  /**
   * The run itself: each case is windowed over its own history, so a row's Nth
   * cell is a different run on every row and no column header can label it.
   */
  run: PartialEvalRun | null;
  outcome: CaseOutcome;
  /** Every iteration of this case in this run, in iteration order. */
  results: PartialEvalCaseResult[];
  passedCount: number;
  /** Iterations that reached a verdict; `passedCount / judgedCount` is the rate. */
  judgedCount: number;
  /** Mean of the iterations' scores. Fills the cell; the outcome is the glyph. */
  meanScore: number | null;
}
/** One row of the case-health matrix: a case and its recent history. */
export interface CaseHealthRow {
  evalCase: PartialEvalCase;
  /** The case's own runs, oldest first. As many as it was exercised in. */
  cells: CaseHealthCell[];
  passedCount: number;
  judgedCount: number;
  /** `passedCount / judgedCount` across the window, or null when never judged. */
  passRate: number | null;
  /**
   * Mean absolute change in the case's pass rate between consecutive judged runs,
   * in [0, 1]. A case that always fails scores 0 — broken, not flaky. Null until
   * two runs judged it.
   */
  flakiness: number | null;
  /**
   * Mean of the case's per-run scores, one value per run so it matches the cells
   * beside it. A run the case never appeared in is left out, not averaged as zero.
   */
  meanScore: number | null;
}
/**
 * The outcome of one case in one run, aggregated over its iterations. `errored`
 * rather than `failed` where the case never reached a judgement, which is what
 * tells a broken case apart from a broken harness.
 */
export function outcomeForResults(
  results: PartialEvalCaseResult[]
): Omit<CaseHealthCell, "runId" | "run"> {
  const judged = results.filter((result) => result.passed != null);
  const passedCount = judged.filter((result) => result.passed).length;
  const counts = {
    results,
    passedCount,
    judgedCount: judged.length,
    meanScore: mean(results.map((result) => result.score).filter(isNumber)),
  };

  if (results.length === 0) {
    return { ...counts, outcome: CaseOutcome.NOT_RUN };
  }
  if (judged.length === 0) {
    // Nothing reached a verdict, so there is nothing to score.
    return { ...counts, outcome: CaseOutcome.ERRORED };
  }
  if (passedCount === judged.length) {
    return { ...counts, outcome: CaseOutcome.PASSED };
  }
  if (passedCount === 0) {
    const allErrored = judged.every((result) => result.error);
    return {
      ...counts,
      outcome: allErrored ? CaseOutcome.ERRORED : CaseOutcome.FAILED,
    };
  }
  return { ...counts, outcome: CaseOutcome.MIXED };
}
/**
 * Mean absolute change in the case's per-run pass *rate* between consecutive
 * judged runs. Not the verdict: at `iterations: 3` one bad iteration turns the
 * cell `mixed`, so counting flips reads a steady 2-of-3 case as flipping every
 * run. Only adjacent judged runs are compared, so a run that excluded the case
 * neither counts as movement nor breaks the chain around it.
 */
function flakinessOf(cells: CaseHealthCell[]): number | null {
  const rates = cells
    .filter((cell) => cell.judgedCount > 0)
    .map((cell) => cell.passedCount / cell.judgedCount);
  if (rates.length < 2) {
    return null;
  }
  let movement = 0;
  for (let i = 1; i < rates.length; i += 1) {
    movement += Math.abs(rates[i] - rates[i - 1]);
  }
  return movement / (rates.length - 1);
}
/** A row's totals over its cells, however the cells were windowed. */
function summarizeCells(evalCase: PartialEvalCase, cells: CaseHealthCell[]): CaseHealthRow {
  const passedCount = cells.reduce((sum, cell) => sum + cell.passedCount, 0);
  const judgedCount = cells.reduce((sum, cell) => sum + cell.judgedCount, 0);

  return {
    evalCase,
    cells,
    passedCount,
    judgedCount,
    passRate: judgedCount > 0 ? passedCount / judgedCount : null,
    flakiness: flakinessOf(cells),
    meanScore: mean(cells.map((cell) => cell.meanScore).filter(isNumber)),
  };
}
/** Sortable start time; an unparseable one sorts oldest rather than unpredictably. */
function startedAtOf(run: PartialEvalRun | null): number {
  const started = parseApiTimestamp(run?.started_at);
  return Number.isNaN(started) ? -Infinity : started;
}
/**
 * Joins cases against each case's *own* newest runs, from the history endpoint.
 *
 * Rows are ragged on purpose: a shared window of the newest runs measured 36%
 * blank, because runs are tag-scoped. Windowing per case spends every cell on a
 * real reading, at the cost of the vertical read. There is no `not_run` cell here
 * by construction, so a case never run at all has no cells.
 */
export function buildCaseHistory(
  cases: PartialEvalCase[],
  results: PartialEvalCaseResult[],
  runs: PartialEvalRun[]
): CaseHealthRow[] {
  const runsById = new Map(runs.map((run) => [run.id, run]));
  const byCaseThenRun = new Map<string, Map<string, PartialEvalCaseResult[]>>();
  for (const result of results) {
    let byRun = byCaseThenRun.get(result.case_id);
    if (!byRun) {
      byRun = new Map();
      byCaseThenRun.set(result.case_id, byRun);
    }
    const existing = byRun.get(result.run_id);
    if (existing) {
      existing.push(result);
    } else {
      byRun.set(result.run_id, [result]);
    }
  }

  return cases.map((evalCase) => {
    const cells = [...(byCaseThenRun.get(evalCase.id)?.entries() ?? [])]
      .map(([runId, iterations]) => ({ runId, run: runsById.get(runId) ?? null, iterations }))
      // Oldest first: the row reads as a progression and the flakiness walk is
      // chronological.
      .sort((a, b) => startedAtOf(a.run) - startedAtOf(b.run))
      .map(({ runId, run, iterations }) => ({
        ...outcomeForResults([...iterations].sort((a, b) => a.iteration - b.iteration)),
        runId,
        run,
      }));

    return summarizeCells(evalCase, cells);
  });
}
/**
 * The evaluators a case would actually be judged by. `expectations` is free-form
 * JSONB, so a case carrying `expected_answer` and nothing else looks configured
 * while being judged by nothing; this resolves the blob the way the runner does,
 * presence tests included — `max_latency_seconds` and `expected_dataframe_count`
 * are read for non-null, so a zero count is an assertion rather than an absence.
 */
export function evaluatorsForCase(
  expectations: Record<string, unknown> | undefined,
  maxLatencySeconds: number | null
): { name: string; label: string; kind: EvaluatorKind }[] {
  const source: Record<string, unknown> = {
    ...(expectations ?? {}),
    max_latency_seconds: maxLatencySeconds,
  };

  return EVALUATOR_ORDER.filter((name) => {
    const { expectationKey, presence } = EVALUATORS[name];
    const value = source[expectationKey];
    return presence === "value" ? value != null : Boolean(value);
  }).map((name) => ({
    name,
    label: EVALUATORS[name].label,
    kind: EVALUATORS[name].kind,
  }));
}
/** The case field carrying the user's question, in the order the suites use it. */
const QUERY_KEYS = ["query", "prompt", "question"] as const;
/**
 * A case's `inputs` split into the question and whatever else it carries, so the
 * question can be shown as prose rather than as an escaped JSON string. The rest
 * comes back separately: a suite is free to put more in `inputs`, and showing
 * only the query would misreport the case.
 */
export function splitCaseInputs(inputs: Record<string, unknown> | undefined): {
  query: string | null;
  rest: Record<string, unknown>;
} {
  if (!inputs) {
    return { query: null, rest: {} };
  }
  const queryKey = QUERY_KEYS.find((key) => {
    const value = inputs[key];
    return typeof value === "string" && value.trim().length > 0;
  });
  if (!queryKey) {
    return { query: null, rest: inputs };
  }
  const { [queryKey]: query, ...rest } = inputs;
  return { query: query as string, rest };
}
/**
 * Row order for the cases table, in either of its column sets. Every ordering
 * leaves nulls at the bottom in both directions — a case that was never judged is
 * an unknown, not the healthiest in the suite — which is `compareNullsLast`'s
 * job, so the direction factor goes to it rather than onto its result.
 */
export function sortCaseRows(
  rows: CaseHealthRow[],
  key: CaseSortKey,
  direction: SortDirection
): CaseHealthRow[] {
  const factor = sortFactor(direction);
  const byText = (a: string, b: string) =>
    factor * a.localeCompare(b, undefined, { sensitivity: "base" });

  return [...rows].sort((a, b) => {
    switch (key) {
      case CaseSortKey.SCORE:
        return compareNullsLast(a.meanScore, b.meanScore, factor);
      case CaseSortKey.PASS_RATE:
        return compareNullsLast(a.passRate, b.passRate, factor);
      case CaseSortKey.FLAKINESS:
        return compareNullsLast(a.flakiness, b.flakiness, factor);
      case CaseSortKey.SUITE:
        return byText(a.evalCase.tags[0] ?? "", b.evalCase.tags[0] ?? "");
      case CaseSortKey.DATASETS:
        return factor * (a.evalCase.dataset_ids.length - b.evalCase.dataset_ids.length);
      case CaseSortKey.THRESHOLD:
        return compareNullsLast(a.evalCase.pass_threshold, b.evalCase.pass_threshold, factor);
      case CaseSortKey.LATENCY:
        return compareNullsLast(
          a.evalCase.max_latency_seconds,
          b.evalCase.max_latency_seconds,
          factor
        );
      case CaseSortKey.UPDATED:
        return (
          factor *
          (parseApiTimestamp(a.evalCase.updated_at) - parseApiTimestamp(b.evalCase.updated_at))
        );
      case CaseSortKey.NAME:
      default:
        return byText(a.evalCase.name, b.evalCase.name);
    }
  });
}
